/**
 * Sidebar fijo con menu agrupado por modulo.
 *
 * - Modulos con sub-items renderizan un header clickeable (boton) con chevron
 *   que abre/cierra el submenu. El modulo con la ruta activa se auto-expande.
 * - El color del sidebar se resuelve por tenant (resolverSidebarTheme) si el
 *   usuario pertenece a un tenant; superusers siempre ven SLATE.
 * - El logo del producto vive en el header del sidebar (asset local) y
 *   cambia a version blanca cuando el tema es de color para mantener contraste.
 */
import { Link, useLocation } from 'react-router-dom';
import { Tooltip } from '@/components/shared/overlays/Tooltip';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { menuSections, canAccessMenuItem, type MenuItem } from '@/config/menu';
import { useAuth } from '@/contexts/AuthContext';
import { resolveSidebarTheme } from '@/config/sidebar-themes';
import { useSidebar } from '@/contexts/SidebarContext';
import rpaLogo from '@/assets/rpa-value-logo.png';
import rpaLogoWhite from '@/assets/rpa-value-logo-white.png';
import { cn } from '@/utils/cn';

const APP_NAME = import.meta.env.VITE_APP_NAME || 'ERPA Core';

// Icono distinto por módulo, sin repetir iconos de items ni de otros módulos.
const moduleIcons: Record<string, string> = {
  general: 'space_dashboard',
  commercial: 'storefront',
  catalog: 'inventory',
  workflow: 'hub',
  'workflow-config': 'settings_applications',
  admin: 'settings',
  superuser: 'shield',
  platform: 'shield',
};

export function Sidebar() {
  const location = useLocation();
  const { user, hasPermission } = useAuth();
  const { collapsed, toggle: toggleCollapsed, setCollapsed } = useSidebar();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  const theme = useMemo(() => {
    if (!user?.active_tenant_id) return resolveSidebarTheme(null);
    return resolveSidebarTheme(user?.tenant_sidebar_theme ?? null);
  }, [user?.active_tenant_id, user?.tenant_sidebar_theme]);

  const isActive = useCallback(
    (path: string) => location.pathname === path || location.pathname.startsWith(path + '/'),
    [location.pathname],
  );

  const visibleSections = useMemo(() => {
    return menuSections
      .map((section) => {
        const items = section.items.filter((item) => {
          if (!canAccessMenuItem(item, hasPermission)) return false;
          return true;
        });
        if (items.length === 0) return null;
        return { ...section, items };
      })
      .filter((s): s is NonNullable<typeof s> => s !== null);
  }, [hasPermission]);

  // Auto-expande el modulo que contiene la ruta activa y colapsa los demas.
  useEffect(() => {
    const activeModule = visibleSections.find((section) =>
      section.items.some((item) => {
        if (isActive(item.path)) return true;
        return item.children?.some((c) => isActive(c.path));
      }),
    )?.module;
    if (activeModule) {
      setExpandedModules(new Set([activeModule]));
    }
  }, [location.pathname, visibleSections, isActive]);

  // Cierra el sidebar mobile con tecla Escape.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const toggleModule = (module: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(module)) next.delete(module);
      else next.add(module);
      return next;
    });
  };

  const handleModuleClick = (module: string) => {
    if (collapsed) {
      setCollapsed(false);
      setExpandedModules(new Set([module]));
      return;
    }
    toggleModule(module);
  };

  const renderLeaf = (item: MenuItem) => {
    const active = isActive(item.path);
    const content = (
      <Link
        to={item.path}
        aria-current={active ? 'page' : undefined}
        onClick={() => setMobileOpen(false)}
        className={cn(
          'no-underline-link flex items-center gap-3 rounded-md py-2 text-sm font-medium transition-colors',
          'pl-9 pr-3',
          active
            ? cn(theme.activeBg, theme.activeText)
            : cn(theme.textBase, theme.hoverBg, theme.hoverText),
        )}
      >
        <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
        <span>{item.label}</span>
      </Link>
    );

    if (collapsed) {
      return (
        <li key={item.key} className="relative">
          <Tooltip title={item.label} placement="right">
            <Link
              to={item.path}
              aria-current={active ? 'page' : undefined}
              onClick={() => setMobileOpen(false)}
              aria-label={item.label}
              className={cn(
                'no-underline-link flex items-center justify-center rounded-md py-3 text-sm font-medium transition-colors',
                active
                  ? cn(theme.activeBg, theme.activeText)
                  : cn(theme.textBase, theme.hoverBg, theme.hoverText),
              )}
            >
              <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
            </Link>
          </Tooltip>
        </li>
      );
    }

    return (
      <li key={item.key} className="relative">
        {content}
      </li>
    );
  };

  const renderItem = (item: MenuItem) => {
    if (item.children && item.children.length > 0) {
      const visibleChildren = item.children.filter(
        (c) => canAccessMenuItem(c, hasPermission),
      );
      if (visibleChildren.length === 0) return null;
      const active = isActive(item.path);
      if (collapsed) {
        return (
          <Tooltip key={item.key} title={item.label} placement="right">
            <button
              type="button"
              onClick={() => handleModuleClick(item.key)}
              aria-label={item.label}
              className={cn(
                'flex w-full items-center justify-center rounded-md py-3 text-sm font-medium transition-colors',
                active
                  ? cn(theme.activeBg, theme.activeText)
                  : cn(theme.textBase, theme.hoverBg, theme.hoverText),
              )}
            >
              <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
            </button>
          </Tooltip>
        );
      }
      return (
        <div key={item.key}>
          <Link
            to={item.path}
            aria-current={active ? 'page' : undefined}
            onClick={() => setMobileOpen(false)}
            className={cn(
              'no-underline-link relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              active
                ? cn(theme.activeBg, theme.activeText)
                : cn(theme.textBase, theme.hoverBg, theme.hoverText),
            )}
          >
            <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
            <span className="flex-1">{item.label}</span>
          </Link>
          <ul className="mt-0.5 space-y-0.5">
            {visibleChildren.map((child) => renderLeaf(child))}
          </ul>
        </div>
      );
    }

    if (collapsed) {
      const active = isActive(item.path);
      return (
        <div key={item.key}>
          <Tooltip title={item.label} placement="right">
            <Link
              to={item.path}
              aria-current={active ? 'page' : undefined}
              onClick={() => setMobileOpen(false)}
              aria-label={item.label}
              className={cn(
                'no-underline-link flex items-center justify-center rounded-md py-3 text-sm font-medium transition-colors',
                active
                  ? cn(theme.activeBg, theme.activeText)
                  : cn(theme.textBase, theme.hoverBg, theme.hoverText),
              )}
            >
              <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
            </Link>
          </Tooltip>
        </div>
      );
    }

    return (
      <div key={item.key}>
        {renderLeaf(item)}
      </div>
    );
  };

  const isColoredTheme = theme.id !== 'SLATE';
  const logoSrc = isColoredTheme ? rpaLogoWhite : rpaLogo;

  return (
    <>
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="fixed left-3 top-3 z-sidebar inline-flex h-10 w-10 items-center justify-center rounded-md bg-white shadow-sm lg:hidden"
        aria-label="Abrir menu"
      >
        <span className="material-symbols-outlined">menu</span>
      </button>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        role="navigation"
        aria-label="Menu principal"
        className={cn(
          'sidebar fixed left-0 top-0 z-50 flex h-screen flex-col border-r transition-all duration-300 ease-in-out',
          theme.bg,
          theme.border,
          collapsed ? 'lg:w-16' : 'lg:w-[220px]',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:translate-x-0',
        )}
      >
        <div
          className={cn(
            'flex h-[60px] items-center justify-between border-b px-3 gap-2',
            theme.bgSubtle,
            theme.border,
          )}
        >
          {!collapsed && (
            <Link
              to="/app/dashboard"
              className="flex min-w-0 items-center justify-center flex-1 no-underline-link"
              aria-label={`Ir al dashboard de ${APP_NAME}`}
            >
              <img src={logoSrc} alt={APP_NAME} className="h-6 w-auto max-w-[120px] object-contain" />
            </Link>
          )}
          {collapsed && (
            <Tooltip title={APP_NAME} placement="right">
              <Link
                to="/app/dashboard"
                className="flex items-center justify-center flex-1 no-underline-link"
                aria-label={`Ir al dashboard de ${APP_NAME}`}
              >
                <img src={logoSrc} alt={APP_NAME} className="h-6 w-auto max-w-[36px] object-contain" />
              </Link>
            </Tooltip>
          )}
          <button
            type="button"
            onClick={() => {
              toggleCollapsed();
              setMobileOpen(false);
            }}
            className={cn(
              'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full shadow-md border transition-colors',
              isColoredTheme
                ?                 'bg-white text-blue-800 border-white hover:bg-slate-100'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50',
            )}
            aria-label={collapsed ? 'Expandir menu' : 'Colapsar menu'}
          >
            <span className="material-symbols-outlined text-[20px]">
              {collapsed ? 'chevron_right' : 'chevron_left'}
            </span>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 sidebar-scrollbar">
          {visibleSections.map((section) => {
            const isExpanded = expandedModules.has(section.module);
            const sectionHasActive = section.items.some((entry) => {
              if (isActive(entry.path)) return true;
              return entry.children?.some((c) => isActive(c.path));
            });
            const sectionIcon = moduleIcons[section.module] || section.items[0]?.icon || 'circle';

            // En colapsado, cada seccion muestra solo su icono con tooltip y expande al click
            if (collapsed) {
              return (
                <div key={section.module} className="mb-2 flex flex-col items-center">
                    <Tooltip title={section.title} placement="right">
                      <button
                        type="button"
                        onClick={() => handleModuleClick(section.module)}
                        aria-expanded={isExpanded}
                        aria-controls={`section-${section.module}`}
                        aria-label={section.title}
                      className={cn(
                        'flex h-10 w-10 items-center justify-center rounded-md transition-colors',
                        theme.textHeading,
                        theme.hoverBg,
                        sectionHasActive && cn(theme.activeBg, theme.activeText),
                      )}
                    >
                      <span className="material-symbols-outlined text-[20px]">{sectionIcon}</span>
                    </button>
                  </Tooltip>
                </div>
              );
            }

            return (
              <div key={section.module} className="mb-4">
                <button
                  type="button"
                  onClick={() => handleModuleClick(section.module)}
                  aria-expanded={isExpanded}
                  aria-controls={`section-${section.module}`}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-xs font-semibold uppercase tracking-wider transition-colors',
                    theme.textHeading,
                    theme.hoverBg,
                  )}
                >
                  <span className="material-symbols-outlined text-[16px]">{sectionIcon}</span>
                  <span className="flex-1">{section.title}</span>
                  <span
                    className={cn(
                      'material-symbols-outlined text-[16px] transition-transform',
                      isExpanded && 'rotate-180',
                      sectionHasActive && 'text-primary',
                    )}
                  >
                    expand_more
                  </span>
                </button>
                {isExpanded && (
                  <div id={`section-${section.module}`} className="mt-1 space-y-0.5">
                    {section.items.map((item) => renderItem(item))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
